import { Tree, TreeNode } from '@fmnts/core';
import { PageDefinition } from './form.types';

/** identifier for root node. */
const ROOT_NODE_VALUE = '<root>';
interface RootNode {
  readonly key: typeof ROOT_NODE_VALUE;
}

/**
 * Base for all Nodes in the Tree:
 *
 * - FormPage
 * - FormFieldset
 * - FormField
 */
export interface FormNode {
  readonly key: string;
}

export type FormDeclarationNode = FormNode | RootNode;

interface Keyed {
  readonly value: FormDeclarationNode;
  readonly children?: ReadonlyArray<Keyed>;
}

export class FormDeclarationTreeNode extends TreeNode<FormDeclarationNode> {
  public static readonly separator = '.';

  public override get children(): FormDeclarationTreeNode[] {
    return this._children as FormDeclarationTreeNode[];
  }

  public get key(): FormDeclarationNode['key'] {
    return this.value.key;
  }

  public findPathBySegments(
    ...segments: ReadonlyArray<string>
  ): FormDeclarationTreeNode[] {
    const { key } = this;
    const [first, ...rest] = segments;

    if (first !== key) {
      return [];
    }

    // First and the current path are matching
    if (rest.length === 0 || (rest.length === 1 && rest[0] === '')) {
      // No more segments to match
      return [this];
    }

    let resultingPath: FormDeclarationTreeNode[] = [];
    for (const child of this.children) {
      // Either use child result, or the previous one
      // This way, templated results are preserved
      const path = child.findPathBySegments(...rest);
      if (path.length) {
        resultingPath = [this, ...path];
        break;
      }
    }

    return resultingPath;
  }

  public findBySegments(
    ...segments: ReadonlyArray<string>
  ): FormDeclarationTreeNode | undefined {
    return TreeNode.popPath(this.findPathBySegments(...segments));
  }
}

function createChildren(
  nodes: ReadonlyArray<Keyed>,
): FormDeclarationTreeNode[] {
  return nodes.map(
    ({ value, children }) =>
      new FormDeclarationTreeNode(
        value,
        children ? createChildren(children) : [],
      ),
  );
}

export class FormDeclarationTree extends Tree<FormDeclarationNode> {
  private static fromDefinition(keyed: ReadonlyArray<Keyed>) {
    return new FormDeclarationTree(
      new FormDeclarationTreeNode(
        { key: ROOT_NODE_VALUE },
        createChildren(keyed),
      ),
    );
  }

  /**
   * Builds a declaration tree from the given `pages`.
   *
   * @param pages
   * @returns
   * An instance of `FormDeclarationTree`.
   */
  public static fromPages(
    pages: readonly PageDefinition[],
  ): FormDeclarationTree {
    return FormDeclarationTree.fromDefinition(pagesToDefinition(pages));
  }

  public override get root(): FormDeclarationTreeNode {
    return this._root as FormDeclarationTreeNode;
  }
}

function pagesToDefinition(pages: readonly PageDefinition[]): Keyed[] {
  return pages.flatMap((p) =>
    p.fieldsets.map((x) => ({
      value: x,
      children: x.fields.map((f) => ({
        value: f,
      })),
    })),
  );
}
